// PDF/image drawing viewer with an SVG measurement overlay.
// Coordinates are PDF user-space units; the raster is rendered at RENDER x.
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import api from "@/lib/api";
import { MEASURE_TYPES, polyLen, polyArea, centroid, fmtQty, computeQty } from "@/lib/takeoffGeo";

import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const RENDER = 1.5;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

// Keep rasters within safe browser canvas limits (max side + total pixels) so
// huge civil plans don't exhaust memory and crash the tab. Returns a scale
// multiplier ≤ 1 to apply on top of RENDER.
const _MAX_SIDE = 4096;
const _MAX_AREA = 24_000_000; // ~24 MP — safe across desktop & mobile
function _capFactor(w, h) {
  const maxSide = Math.max(w, h);
  const area = w * h;
  const f = Math.min(1, _MAX_SIDE / maxSide, Math.sqrt(_MAX_AREA / area));
  return isFinite(f) && f > 0 ? f : 1;
}

const docCache = {}; // drawing.id -> Promise<pdfDoc | HTMLImageElement>

async function loadDoc(drawing) {
  if (!docCache[drawing.id]) {
    docCache[drawing.id] = (async () => {
      const res = await api.get("/files/view", {
        params: { path: drawing.file_path },
        responseType: "arraybuffer",
      });
      if (drawing.kind === "pdf") {
        return pdfjsLib.getDocument({ data: res.data }).promise;
      }
      const blob = new Blob([res.data], { type: drawing.content_type });
      const img = new Image();
      img.src = URL.createObjectURL(blob);
      await new Promise((ok, err) => { img.onload = ok; img.onerror = err; });
      return img;
    })().catch((e) => { delete docCache[drawing.id]; throw e; });
  }
  return docCache[drawing.id];
}

export default function TakeoffCanvas({
  drawing, page, tool, layers, measurements, selectedId, onSelect,
  onShapeComplete, mPerUnit, focusRegion,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [dims, setDims] = useState(null); // {w, h} doc units
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState({ zoom: 1, ox: 0, oy: 0 });
  const [draft, setDraft] = useState([]); // in-progress points
  const [cursor, setCursor] = useState(null);
  const panRef = useRef(null);
  const renderTaskRef = useRef(null);
  const movedRef = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const toolRef = useRef(tool);

  const layerColor = {};
  (layers || []).forEach((l) => { layerColor[l.id] = l.color; });
  const visible = new Set((layers || []).filter((l) => l.visible !== false).map((l) => l.id));

  const fit = useCallback((w, h) => {
    const el = wrapRef.current;
    if (!el || !w) return;
    const z = Math.min(el.clientWidth / (w * RENDER), el.clientHeight / (h * RENDER)) * 0.95;
    setView({
      zoom: clamp(z, 0.05, 10),
      ox: (el.clientWidth - w * RENDER * z) / 2,
      oy: (el.clientHeight - h * RENDER * z) / 2,
    });
  }, []);

  // render the page
  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true); setError(null); setDraft([]);
      // Cancel any render still running on this canvas — starting a second
      // render on the same <canvas> throws in pdf.js and can crash the tab.
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* already done */ }
        renderTaskRef.current = null;
      }
      try {
        const doc = await loadDoc(drawing);
        if (dead) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (drawing.kind === "pdf") {
          const pg = await doc.getPage(page);
          if (dead) return;
          const full = pg.getViewport({ scale: RENDER });
          // Cap the raster so it never exceeds the browser's max canvas size /
          // memory budget — large-format civil plans (A1/A0, high-DPI scans)
          // otherwise blow past ~4096px per side and crash on render.
          const cap = _capFactor(full.width, full.height);
          const raster = cap < 1 ? pg.getViewport({ scale: RENDER * cap }) : full;
          canvas.width = Math.floor(raster.width);
          canvas.height = Math.floor(raster.height);
          // Display at full coordinate size so the SVG overlay stays aligned.
          canvas.style.width = `${full.width}px`;
          canvas.style.height = `${full.height}px`;
          const task = pg.render({ canvasContext: canvas.getContext("2d"), viewport: raster });
          renderTaskRef.current = task;
          await task.promise;
          renderTaskRef.current = null;
          if (dead) return;
          const d = { w: full.width / RENDER, h: full.height / RENDER };
          setDims(d); fit(d.w, d.h);
        } else {
          const fullW = doc.naturalWidth * RENDER, fullH = doc.naturalHeight * RENDER;
          const cap = _capFactor(fullW, fullH);
          canvas.width = Math.floor(fullW * cap);
          canvas.height = Math.floor(fullH * cap);
          canvas.style.width = `${fullW}px`;
          canvas.style.height = `${fullH}px`;
          canvas.getContext("2d").drawImage(doc, 0, 0, canvas.width, canvas.height);
          const d = { w: doc.naturalWidth, h: doc.naturalHeight };
          setDims(d); fit(d.w, d.h);
        }
      } catch (e) {
        // A cancelled render is expected when flipping pages fast — not an error.
        if (!dead && e?.name !== "RenderingCancelledException") setError("Could not load the drawing");
      } finally { if (!dead) setLoading(false); }
    })();
    return () => {
      dead = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* already done */ }
        renderTaskRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing.id, page]);

  // clear the draft when the tool changes
  useEffect(() => {
    if (toolRef.current !== tool) { toolRef.current = tool; setDraft([]); }
  }, [tool]);

  // AI "locate on plan" — zoom to region(s) and flash an orange highlight
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!focusRegion?.rects?.length || !dims || !wrapRef.current) return;
    const rects = focusRegion.rects.map((r) => ({
      x: r.x * dims.w, y: r.y * dims.h,
      w: Math.max(r.w || 0.02, 0.01) * dims.w, h: Math.max(r.h || 0.02, 0.01) * dims.h,
    }));
    const x0 = Math.min(...rects.map((r) => r.x)), y0 = Math.min(...rects.map((r) => r.y));
    const x1 = Math.max(...rects.map((r) => r.x + r.w)), y1 = Math.max(...rects.map((r) => r.y + r.h));
    const padW = (x1 - x0) * 0.6 + dims.w * 0.03, padH = (y1 - y0) * 0.6 + dims.h * 0.03;
    const uw = (x1 - x0) + padW * 2, uh = (y1 - y0) + padH * 2;
    const el = wrapRef.current;
    const z = clamp(Math.min(el.clientWidth / (uw * RENDER), el.clientHeight / (uh * RENDER)), 0.05, 8);
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    setView({ zoom: z, ox: el.clientWidth / 2 - cx * RENDER * z, oy: el.clientHeight / 2 - cy * RENDER * z });
    setFlash(rects);
    const timer = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRegion?.key, dims]);

  const toDoc = useCallback((e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const v = viewRef.current;
    return {
      x: (e.clientX - rect.left - v.ox) / (v.zoom * RENDER),
      y: (e.clientY - rect.top - v.oy) / (v.zoom * RENDER),
    };
  }, []);

  // non-passive wheel zoom toward the cursor
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const v = viewRef.current;
      const f = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const nz = clamp(v.zoom * f, 0.05, 12);
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      setView({ zoom: nz, ox: mx - (mx - v.ox) * (nz / v.zoom), oy: my - (my - v.oy) * (nz / v.zoom) });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // keyboard: Enter finish, Esc cancel, Backspace undo point
  // finishRef always points at the latest closure (avoids stale onShapeComplete)
  const finishRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const pts = draftRef.current;
      if (e.key === "Escape") setDraft([]);
      if (e.key === "Backspace" && pts.length) { e.preventDefault(); setDraft(pts.slice(0, -1)); }
      if (e.key === "Enter" && pts.length) finishRef.current?.(pts);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const finishDraft = (pts) => {
    const t = toolRef.current;
    const cfg = MEASURE_TYPES[t];
    const min = t === "calibrate" ? 2 : (cfg?.min || 2);
    // dedupe consecutive near-identical points (double-click artefact)
    const clean = pts.filter((p, i) => i === 0 || Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y) > 0.5);
    if (clean.length < min) { setDraft([]); return; }
    setDraft([]);
    onShapeComplete(t, clean);
  };
  finishRef.current = finishDraft;

  const isDrawTool = tool === "calibrate" || !!MEASURE_TYPES[tool];

  const onPointerDown = (e) => {
    if (tool === "pan" || e.button === 1 || (e.button === 0 && !isDrawTool && tool !== "select")) {
      panRef.current = { x: e.clientX, y: e.clientY, ox: view.ox, oy: view.oy };
      movedRef.current = 0;
      e.currentTarget.setPointerCapture(e.pointerId);
    } else if (e.button === 0 && tool === "select") {
      panRef.current = { x: e.clientX, y: e.clientY, ox: view.ox, oy: view.oy };
      movedRef.current = 0;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };
  const onPointerMove = (e) => {
    setCursor(toDoc(e));
    if (panRef.current) {
      const dx = e.clientX - panRef.current.x, dy = e.clientY - panRef.current.y;
      movedRef.current = Math.max(movedRef.current, Math.abs(dx) + Math.abs(dy));
      if (movedRef.current > 3) setView((v) => ({ ...v, ox: panRef.current.ox + dx, oy: panRef.current.oy + dy }));
    }
  };
  const onPointerUp = () => { panRef.current = null; };

  const onClick = (e) => {
    if (movedRef.current > 3) return; // it was a pan
    if (!isDrawTool) return;
    const p = toDoc(e);
    if (!dims || p.x < 0 || p.y < 0 || p.x > dims.w || p.y > dims.h) return;
    const next = [...draft, p];
    if (tool === "calibrate" && next.length === 2) { finishDraft(next); return; }
    setDraft(next);
  };
  const onDblClick = () => { if (draft.length) finishDraft(draft); };

  // live label for the draft
  let draftLabel = null;
  if (draft.length >= 1 && cursor && isDrawTool && tool !== "count") {
    const pts = [...draft, cursor];
    if (["length", "trench", "calibrate"].includes(tool)) {
      const d = polyLen(pts);
      draftLabel = mPerUnit ? `${fmtQty(d * mPerUnit)} m` : `${fmtQty(d)} units`;
    } else if (pts.length >= 3) {
      const a = polyArea(pts);
      draftLabel = mPerUnit ? `${fmtQty(a * mPerUnit * mPerUnit)} m²` : `${fmtQty(a)} units²`;
    }
  }

  const px = (n) => n / (view.zoom * RENDER); // constant screen size in doc units
  const shapeMs = (measurements || []).filter(
    (m) => m.drawing_id === drawing.id && (m.page || 1) === page &&
      (!m.layer_id || visible.has(m.layer_id))
  );

  const cursorCls = tool === "pan" ? "cursor-grab" : isDrawTool ? "cursor-crosshair" : "cursor-default";

  return (
    <div ref={wrapRef} className={`relative flex-1 min-h-0 overflow-hidden bg-zinc-200 dark:bg-[#06090D] ${cursorCls}`}
      data-testid="takeoff-canvas"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
      onClick={onClick} onDoubleClick={onDblClick}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/20">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5F15]" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 z-10 flex items-center justify-center text-sm text-red-400">{error}</div>
      )}
      <div style={{ transform: `translate(${view.ox}px, ${view.oy}px) scale(${view.zoom})`, transformOrigin: "0 0", width: dims ? dims.w * RENDER : 0 }}>
        <canvas ref={canvasRef} className="block shadow-2xl bg-white" />
        {dims && (
          <svg className="absolute top-0 left-0" width={dims.w * RENDER} height={dims.h * RENDER}
            viewBox={`0 0 ${dims.w} ${dims.h}`} style={{ overflow: "visible" }}>
            {shapeMs.map((m, mi) => {
              const color = layerColor[m.layer_id] || "#FF5F15";
              const sel = m.id === selectedId;
              const cfg = MEASURE_TYPES[m.type] || {};
              const ptsStr = (m.points || []).map((p) => `${p.x},${p.y}`).join(" ");
              const c0 = centroid(m.points || []);
              // stack labels of overlapping measurements so they stay readable
              const stack = shapeMs.slice(0, mi).filter((o) => {
                const oc = centroid(o.points || []);
                return Math.abs(oc.x - c0.x) < px(80) && Math.abs(oc.y - c0.y) < px(20);
              }).length;
              const c = { x: c0.x, y: c0.y + stack * px(22) };
              const label = `${fmtQty(m.qty)} ${m.unit || ""}`;
              const click = (e) => { e.stopPropagation(); onSelect(sel ? null : m.id); };
              return (
                // shapes only capture clicks in Select mode — with a measuring tool active they
                // must stay click-through so you can mark up over an existing measurement
                <g key={m.id} onClick={click} pointerEvents={tool === "select" ? "auto" : "none"}
                  className={tool === "select" ? "cursor-pointer" : ""}>
                  {["count", "structure"].includes(m.type) ? (
                    (m.points || []).map((p, i) => (
                      <g key={i}>
                        <circle cx={p.x} cy={p.y} r={px(sel ? 9 : 7)} fill={color} fillOpacity={0.85}
                          stroke={sel ? "#fff" : color} strokeWidth={px(2)} />
                        <text x={p.x} y={p.y + px(4)} textAnchor="middle" fontSize={px(10)} fill="#fff" fontWeight="700">{i + 1}</text>
                      </g>
                    ))
                  ) : cfg.closed ? (
                    <polygon points={ptsStr} fill={color} fillOpacity={sel ? 0.35 : 0.2}
                      stroke={color} strokeWidth={px(sel ? 3 : 2)} strokeDasharray={sel ? `${px(6)} ${px(4)}` : undefined} />
                  ) : (
                    <>
                      <polyline points={ptsStr} fill="none" stroke="transparent" strokeWidth={px(14)} />
                      <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={px(sel ? 4 : 2.5)}
                        strokeDasharray={sel ? `${px(6)} ${px(4)}` : undefined} strokeLinecap="round" strokeLinejoin="round" />
                    </>
                  )}
                  {!["count", "structure"].includes(m.type) && (
                    <g>
                      <rect x={c.x - px(label.length * 3.4 + 6)} y={c.y - px(9)} width={px(label.length * 6.8 + 12)} height={px(18)}
                        rx={px(3)} fill="#0B0F14" fillOpacity={0.85} />
                      <text x={c.x} y={c.y + px(4)} textAnchor="middle" fontSize={px(11)} fill={color} fontWeight="700" fontFamily="monospace">{label}</text>
                    </g>
                  )}
                </g>
              );
            })}
            {/* AI locate highlight */}
            {flash && flash.map((r, i) => (
              <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} pointerEvents="none"
                fill="#FF5F15" fillOpacity={0.18} stroke="#FF5F15" strokeWidth={px(3)}
                className="animate-pulse" data-testid="ai-locate-highlight" />
            ))}
            {/* in-progress draft */}
            {draft.length > 0 && (
              <g pointerEvents="none">
                <polyline points={[...draft, ...(cursor && tool !== "count" ? [cursor] : [])].map((p) => `${p.x},${p.y}`).join(" ")}
                  fill="none" stroke="#FF5F15" strokeWidth={px(2.5)} strokeDasharray={`${px(6)} ${px(4)}`} />
                {["area", "volume"].includes(tool) && draft.length >= 2 && cursor && (
                  <line x1={cursor.x} y1={cursor.y} x2={draft[0].x} y2={draft[0].y} stroke="#FF5F15" strokeWidth={px(1.5)} strokeDasharray={`${px(3)} ${px(3)}`} />
                )}
                {draft.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={px(4)} fill="#FF5F15" stroke="#fff" strokeWidth={px(1.5)} />
                ))}
                {draftLabel && cursor && (
                  <text x={cursor.x + px(14)} y={cursor.y - px(10)} fontSize={px(12)} fill="#FF5F15" fontWeight="800" fontFamily="monospace"
                    stroke="#0B0F14" strokeWidth={px(3)} paintOrder="stroke">{draftLabel}</text>
                )}
              </g>
            )}
          </svg>
        )}
      </div>
      {/* zoom controls */}
      <div className="absolute bottom-3 right-3 flex flex-col gap-1 z-10">
        {[{ Icon: ZoomIn, f: 1.3, id: "zoom-in" }, { Icon: ZoomOut, f: 1 / 1.3, id: "zoom-out" }].map(({ Icon, f, id }) => (
          <button key={id} data-testid={`takeoff-${id}`}
            onClick={(e) => {
              e.stopPropagation();
              const el = wrapRef.current;
              const mx = el.clientWidth / 2, my = el.clientHeight / 2;
              setView((v) => {
                const nz = clamp(v.zoom * f, 0.05, 12);
                return { zoom: nz, ox: mx - (mx - v.ox) * (nz / v.zoom), oy: my - (my - v.oy) * (nz / v.zoom) };
              });
            }}
            className="w-9 h-9 flex items-center justify-center bg-[#0B0F14]/90 text-zinc-300 hover:text-[#FF5F15] border border-white/10 backdrop-blur">
            <Icon className="w-4 h-4" />
          </button>
        ))}
        <button data-testid="takeoff-zoom-fit" onClick={(e) => { e.stopPropagation(); if (dims) fit(dims.w, dims.h); }}
          className="w-9 h-9 flex items-center justify-center bg-[#0B0F14]/90 text-zinc-300 hover:text-[#FF5F15] border border-white/10 backdrop-blur">
          <Maximize className="w-4 h-4" />
        </button>
      </div>
      {/* draft hint */}
      {draft.length > 0 && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 bg-[#0B0F14]/90 border border-[#FF5F15]/40 text-[11px] font-bold uppercase tracking-wider text-zinc-200 backdrop-blur" data-testid="takeoff-draft-hint">
          {tool === "count" ? `${draft.length} counted — ` : `${draft.length} point${draft.length > 1 ? "s" : ""} — `}
          double-click or Enter to finish · Esc to cancel
        </div>
      )}
    </div>
  );
}
